import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/navigation";
import { Navigation } from "swiper/modules";
import { ArrowUpRight, CaretRight, CaretLeft } from "@phosphor-icons/react";
import "./style.css";

const Projects = ({ data }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [activeSlideIndex, setActiveSlideIndex] = useState(0);
  const [filteredData, setFilteredData] = useState(data);
  const [selectedCategory, setSelectedCategory] = useState(null);

  const prevRef = useRef(null);
  const nextRef = useRef(null);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        setIsVisible(entry.isIntersecting);
      },
      {
        threshold: 0.5,
      }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const handleCategoryClick = (category) => {
    if (selectedCategory === category) {
      // Reset to the original data when clicking the same category again
      setFilteredData(data);
      setSelectedCategory(null);
    } else {
      // Filter the data by the selected category
      setFilteredData(data.filter((item) => item.category === category));
      setSelectedCategory(category);
    }
  };

  useEffect(() => {
    setFilteredData(data);
  }, [data]);

  return (
    <div
      ref={sectionRef}
      className="relative scroll-section h-[80vh] lg:h-[100vh]"
    >
      <div className="text-center uppercase absolute px-10 lg:left-1/2 lg:-translate-x-1/2 top-20 z-[100]">
        <p className="text-white text-[#b8b8b8] text-xl">Our</p>
        <h4 className="title">Projects</h4>

        <div className="flex justify-center items-center gap-2 lg:justify-between flex-wrap lg:flex-nowrap mt-2 text-xl">
          {Array.from(new Set(data?.map(({ category }) => category))).map(
            (category, index) => (
              <button
                key={index}
                onClick={() => handleCategoryClick(category)}
                className={`lg:flex-1 text-center text-white px-6 lg:min-w-max font-semibold capitalize ${
                  selectedCategory === category ? "border border-white" : ""
                }`}
              >
                {category}
              </button>
            )
          )}
        </div>
      </div>
      <Swiper
        className="h-full"
        spaceBetween={20}
        slidesPerView={1}
        navigation={{
          prevEl: prevRef.current,
          nextEl: nextRef.current,
        }}
        onBeforeInit={(swiper) => {
          swiper.params.navigation.prevEl = prevRef.current;
          swiper.params.navigation.nextEl = nextRef.current;
        }}
        onSlideChange={(swiper) => setActiveSlideIndex(swiper.activeIndex)}
        speed={1200}
        modules={[Navigation]}
      >
        {filteredData?.map(({ image, title, text, slug }, index) => (
          <SwiperSlide
            key={index}
            className="h-full project-swiper"
            style={{
              backgroundImage: `url(${image})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          >
            <div
              className={`text-white bg-[#000000a3] mt-auto w-full h-auto lg:h-full lg:w-1/2 lg:ml-auto flex flex-col lg:justify-center p-12 transition-transform duration-1000 ${
                isVisible && activeSlideIndex === index
                  ? "translate-x-0 opacity-100"
                  : "translate-x-[100%] opacity-50"
              }`}
            >
              <p className="bebas text-4xl lg:w-1/2 mt-20">{title}</p>
              <p className="mt-2 text-xl lg:w-1/2">{text}</p>
              <Link
                className="flex items-center gap-x-2 mt-8 hover:underline"
                to={`/projects/${slug}`}
              >
                <ArrowUpRight weight="thin" size={32} />
                View Details
              </Link>
            </div>
          </SwiperSlide>
        ))}
      </Swiper>
      <div className="absolute z-[100] right-0 text-white top-1/2 -translate-y-1/2 flex flex-col">
        <button
          ref={prevRef}
          className="slider-arrow bg-black p-3 border border-[#b8b8b8] text-2xl"
        >
          <CaretLeft />
        </button>
        <button
          ref={nextRef}
          className="slider-arrow bg-black p-3 border border-[#b8b8b8] text-2xl"
        >
          <CaretRight />
        </button>
      </div>
    </div>
  );
};

export default Projects;
